import { inject, Injectable } from '@angular/core';
import { UserService } from 'app/core/user/user.service';
import { firstValueFrom, map, Observable, of, switchMap } from 'rxjs';
import { AgaveAuthService, AgaveService, IAgaveError, nonNull } from 'tq-agave';
import { SignInInfo } from './sign-in-info';
import { Company } from 'app/db/Company';
import { CompanyUser_B } from 'app/db/CompanyUser_B';
import { GlobalsService } from 'app/globals.service';
import { ChangePasswordDialogComponent, ChangePasswordDialogResult } from 'app/dialogs/change-password-dialog/change-password-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _userService = inject(UserService);
    private _agaveAuthService = inject(AgaveAuthService);
    private _agaveService = inject(AgaveService);
    private _globalsService = inject(GlobalsService);
    private _matDialog = inject(MatDialog);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    // set accessToken(token: string)
    // {
    //     localStorage.setItem('accessToken', token);
    // }
    //
    // get accessToken(): string
    // {
    //     return localStorage.getItem('accessToken') ?? '';
    // }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        // TODO implement
        window.alert('Not implemented yet!');
        return of({});
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        // TODO implement
        window.alert('Not implemented yet!');
        return of({});
    }



    /**
     * Ask for and change password - apre il dialog per il cambio password
     * richiede action json_rpc_modify_password_user_imsign --> DoJsonRpcModifyConfirmPasswordUserImsign
     */
    askForAndChangePassword(): Observable<boolean> {
        return this._matDialog
            .open(ChangePasswordDialogComponent, {
                width: '560px',
                maxWidth: '95vw',
                autoFocus: false,
                disableClose: true,
            })
            .afterClosed()
            .pipe(
                switchMap((result: ChangePasswordDialogResult | undefined) => {
                    if (!result) {
                        return of(false);
                    }

                    return this.changePasswordVeryfyOldPassword(result.oldPassword, result.newPassword).pipe(  //usa changePassword() se non vuoi verificare la vecchia password
                        map(() => true)
                    );
                })
            );
    }

    changePassword(password: string): Observable<any> {
        return new Observable((observer) => {
            this._agaveService.agave({
                _page: 'initial',
                _state: 'initial',
                _action_json_rpc_modify_password_user_imsign: '1',
                name: this._agaveService.currentUser.username,
                password_: password,
            }).subscribe({
                next: () => {
                    this._globalsService.openSnackBar('Password cambiata con successo.', 'Ok');
                    observer.next(null);
                    observer.complete();
                },
                error: (error: any) => {
                    this._globalsService.openErrorDialog(error, 'Errore');
                    observer.error(error);
                },
            });
        });
    }


    changePasswordVeryfyOldPassword(oldPassword: string, newPassword: string, showErrorDialog: boolean = true): Observable<any> {
        return new Observable((observer) => {
            this._agaveService.agave({
                _page: 'initial',
                _state: 'initial',
                _action_json_rpc_modify_password_user_imsign: '1',
                name: this._agaveService.currentUser.username,
                old_password_: oldPassword,
                password_: newPassword,
                verify_old_password_01: '1',
            }).subscribe({
                next: () => {
                    this._globalsService.openSnackBar('Password changed successfully.', 'Ok');
                    observer.next(null);
                    observer.complete();
                },
                error: (error: any) => {
                    let errorMessage = '';
                    if(error.jsonRpcCode && error.jsonRpcCode === -500 && error.message && error.message === 'it.imteam.cadmoj.CadmoError: Vecchia password errata') {
                        errorMessage = 'The old password is incorrect.';
                        if(showErrorDialog) {
                            this._globalsService.openErrorDialogString(errorMessage, 'Error');
                        }

                        observer.error(errorMessage);
                        throw new Error(errorMessage);
                    } else {
                        if(showErrorDialog) {
                            this._globalsService.openErrorDialog(error, 'Errore');
                        }
                        observer.error(error);
                        throw new Error('Error changing password: ' + error);
                    }


                },
            });
        });
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    async signIn(credentials: SignInInfo): Promise<void> {
        // Throw error, if the user is already logged in
        if (this._agaveAuthService.getIsLogged()) {
            console.log('User already logged in, setting user from existing session');
            if (this._agaveAuthService.getBooleanParameterValue('company_manager')) {
                this.setUser();
            } else {
                if (!this._userService.user.company) {
                    this.authenticateUser();
                } else {
                    this.setUser();
                }
            }
            return;
        }

        try {
            await this._agaveAuthService.authenticate(
                credentials.username,
                credentials.password,
                credentials.rememberMe ?? false
            );
        } catch (e: unknown) {
            console.error('Errore durante l\'autenticazione:', e);

            if (typeof e === 'object' && nonNull(e) && 'agaveErrorCode' in e) {
                const agaveError = (e as { agaveErrorCode: string, jsonRpcCode: number });
                const code = agaveError.agaveErrorCode;
                const jsonRpcCode = agaveError.jsonRpcCode;

                if (code === '010') {
                    throw new Error('The server is not responding');
                } else {
                    if (jsonRpcCode === -10) {
                        throw new Error('Incorrect username or password');
                    } else if (jsonRpcCode === -13) {
                        throw new Error('User temporarily blocked due to too many failed attempts, please try again later');
                    } else {
                        throw new Error(`Error during authentication: ${code} (json rpc code: ${jsonRpcCode})`);
                    }
                }
            } else if (typeof e === 'string' || e instanceof String) {
                if ((e as string).indexOf('010Response with status: 0') >= 0) {
                    throw new Error('The server is not responding');
                } else {
                    throw new Error('Incorrect username or password');
                }
            } else {
                throw new Error(`Unspecified error during authentication: ` + e);
            }
        }

        await this.authenticateUser();

        if (credentials.password === '00000000') {

            const authKey = localStorage.getItem('authentication_key');

            if (!authKey) {
                this._agaveAuthService.weakLogOut();
                return;
            }

            const result = await firstValueFrom(
                this._matDialog.open(ChangePasswordDialogComponent, {
                    panelClass: 'change-password-dialog',
                    data: {
                        force: true,
                        oldPassword: credentials.password,
                    }
                }).afterClosed()
            );

            if (result) {
                try {
                    await firstValueFrom(this.changePasswordVeryfyOldPassword(result.oldPassword, result.newPassword, false));
                } catch (err) {
                    this._agaveAuthService.weakLogOut();
                    throw err;
                }
            } else {
                this._agaveAuthService.weakLogOut();
                throw new Error(`You must change your password before using the application!`);
            }

            // qui continua SOLO dopo la chiusura del dialog
        }
    }

    async authenticateUser(): Promise<void> {
        if (this._agaveAuthService.getBooleanParameterValue('company_manager')) {
            if (this._agaveAuthService.getBooleanParameterValue('reader')) {
                this.setUser();
            } else {
                await this._agaveAuthService.weakLogOut();
                throw new Error(`The user does not have permissions to use the application, it needs at least reader clearance!`);
            }
        } else if (this._agaveAuthService.getBooleanParameterValue('reader')) {
            const proto: CompanyUser_B = new CompanyUser_B();
            proto.email = this._agaveAuthService.getUsername();

            try {
                const user = await firstValueFrom(proto.uniqueFromProto(this._agaveService));

                if (user && user.company_id && user.to_one_company) {
                    this.setUserWithCompany(user.to_one_company);
                } else {
                    await this._agaveAuthService.weakLogOut();
                    console.error('User not found in rulmeca system');
                    throw new Error('User not found');
                }
            } catch (err) {
                await this._agaveAuthService.weakLogOut();
                console.error('User not found in rulmeca system:', err);
                throw new Error('User not found');
            }
        } else {
            await this._agaveAuthService.weakLogOut();
            throw new Error('The user does not have permissions to use the application, it needs at least reader clearance!');
        }

    }

    /**
     * Check the authentication status
     */
    async check(): Promise<boolean> {
        // Check if the user is logged in

        try {
            if (!this._agaveAuthService.getIsLogged()) {
                await this._agaveAuthService.autoAuthentication();
            }

            await this.authenticateUser();
            return true;
        } catch (e) {
            return false;
        }
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        // return this._httpClient.post('api/auth/refresh-access-token', {
        //     accessToken: this.accessToken
        // }).pipe(
        //     catchError(() =>
        //
        //         // Return false
        //         of(false)
        //     ),
        //     switchMap((response: any) => {
        //
        //         // Store the access token in the local storage
        //         this.accessToken = response.accessToken;
        //
        //         // Set the authenticated flag to true
        //         this._authenticated = true;
        //
        //         // Store the user on the user service
        //         this._userService.user = response.user;
        //
        //         // Return true
        //         return of(true);
        //     })
        // );
        window.alert('Not implemented yet!');
        return of({});
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        this._agaveAuthService.weakLogOut();
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        window.alert('Not implemented yet!');
        return of({});
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        window.alert('Not implemented yet!');
        return of({});
    }


    setUser(): void {
        this._userService.user = {
            username: this._agaveAuthService.getUsername(),
            description: this._agaveAuthService.getUserDescription(),
            avatar: 'images/avatars/brian-hughes.jpg'
        };
    }

    setUserWithCompany(company: Company): void {
        this._userService.user = {
            username: this._agaveAuthService.getUsername(),
            description: this._agaveAuthService.getUserDescription(),
            avatar: 'images/avatars/brian-hughes.jpg',
            company: company
        };
    }
}
